import axios from 'axios'

// Base URL for the API - uses REACT_APP_NEW_BASE_URL from .env
const API_BASE_URL = process.env.REACT_APP_NEW_BASE_URL || 'http://localhost:6500'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle MongoDB ObjectId format
apiClient.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = transformMongoDBData(response.data)
    }
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    throw error
  }
)

// Helper function to transform MongoDB data
const transformMongoDBData = (data) => {
  if (Array.isArray(data)) {
    return data.map(transformMongoDBData)
  } else if (data && typeof data === 'object') {
    if (data.$oid) {
      return data.$oid
    }
    if (data.$date) {
      return new Date(data.$date).toISOString()
    }
    if (data.$numberLong) {
      return parseInt(data.$numberLong, 10)
    }
    const transformed = {}
    for (const key in data) {
      transformed[key] = transformMongoDBData(data[key])
    }
    return transformed
  }
  return data
}

// DGR Service Functions

// Fetch all reports with optional filters
export const fetchDGRReports = async (filters = {}) => {
  try {
    const params = new URLSearchParams()
    
    if (filters.startDate) params.append('startDate', filters.startDate)
    if (filters.endDate) params.append('endDate', filters.endDate)
    if (filters.plantId) params.append('plantId', filters.plantId)
    if (filters.plantName) params.append('plantName', filters.plantName)

    const response = await apiClient.get('/api/daily-generation-reports', { params })
    return response.data.data || []
  } catch (error) {
    console.error('Error fetching DGR reports:', error)
    throw error
  }
}

// Fetch single report by ID
export const fetchDGRById = async (dgrId) => {
  try {
    const response = await apiClient.get(`/api/daily-generation-reports/${dgrId}`)
    return response.data.data
  } catch (error) {
    console.error('Error fetching DGR report:', error)
    throw error
  }
}

// Create new DGR report
export const createDGRReport = async (dgrData) => {
  try {
    const response = await apiClient.post('/api/daily-generation-reports', dgrData)
    return response.data.data
  } catch (error) {
    console.error('Error creating DGR report:', error)
    throw error
  }
}

// Update existing DGR report
export const updateDGRReport = async (dgrId, dgrData) => {
  try {
    const response = await apiClient.put(`/api/daily-generation-reports/${dgrId}`, dgrData)
    return response.data.data
  } catch (error) {
    console.error('Error updating DGR report:', error)
    throw error
  }
}

// Delete DGR report
export const deleteDGRReport = async (dgrId) => {
  try {
    const response = await apiClient.delete(`/api/daily-generation-reports/${dgrId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting DGR report:', error)
    throw error
  }
}

// Fetch plant list for form dropdowns (combined DB + hardcoded plants)
export const fetchPlantListForDGR = async (reportDate) => {
  try {
    const params = {}
    if (reportDate) {
      params.reportDate = reportDate
    }
    const response = await apiClient.get('/api/daily-generation-reports/form/plants', { params })
    return response.data.plants || []
  } catch (error) {
    console.error('Error fetching plants:', error)
    throw error
  }
}

// Fetch auto-calculated metrics for a specific plant and date
export const fetchPlantMetrics = async (plantId, reportDate) => {
  try {
    const response = await apiClient.get(
      `/api/daily-generation-reports/metrics/${plantId}/${reportDate}`
    )
    const data = response.data || {}

    // Helper to safely convert values (including null/undefined/empty) to numbers (0 if missing)
    const toNumber = (v) => {
      if (v === '' || v === null || v === undefined) return 0
      const n = Number(v)
      return Number.isNaN(n) ? 0 : n
    }

    const isHardcoded = !!data.isHardcoded

    // If plant is hardcoded or no metrics available, return empty metrics object
    if (isHardcoded || !data.metrics || Object.keys(data.metrics).length === 0) {
      return {
        success: data.success ?? true,
        metrics: {},
        isHardcoded: isHardcoded,
        message: data.message || 'Hardcoded plant - please enter metrics manually',
      }
    }

    // Clean metrics: ensure all fields are numbers and missing values become 0
    const m = data.metrics || {}
    const cleanMetrics = {
      gti: toNumber(m.gti),
      actYield: toNumber(m.actYield),
      actGeneration: toNumber(m.actGeneration),
      pr: toNumber(m.pr),
    }

    return {
      success: data.success ?? true,
      metrics: cleanMetrics,
      isHardcoded: false,
      message: data.message || 'Metrics fetched from database',
    }
  } catch (error) {
    console.error('Error fetching plant metrics:', error)
    return null
  }
}

//DGR import from excel/csv file
export const importDailyGenerationReports = async (file, month) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)

    const response = await axios.post(
      `${API_BASE_URL}api/daily-generation-reports/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    )

    return response.data
  } catch (error) {
    console.error('Error importing daily generation reports:', error)
    throw error
  }
}

export default {
  fetchDGRReports,
  fetchDGRById,
  createDGRReport,
  updateDGRReport,
  deleteDGRReport,
  fetchPlantListForDGR,
  fetchPlantMetrics,
  importDailyGenerationReports,
}
